import { Controller } from "@hotwired/stimulus";
import Fuse from "fuse.js";

export default class extends Controller {
  static targets = ["input", "results", "resultsList", "noResults"];
  static values = { index: Array };

  connect() {
    this.selectedIndex = -1;
    this.fuse = new Fuse(this.indexValue, {
      keys: [
        { name: "title", weight: 0.4 },
        { name: "description", weight: 0.3 },
        { name: "keywords", weight: 0.3 },
      ],
      threshold: 0.4,
      includeScore: true,
    });

    this.handleGlobalKeydown = this.globalKeydown.bind(this);
    this.handleClickOutside = this.clickOutside.bind(this);
    document.addEventListener("keydown", this.handleGlobalKeydown);
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleGlobalKeydown);
    document.removeEventListener("click", this.handleClickOutside);
  }

  globalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.inputTarget.focus();
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  }

  search() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.performSearch();
    }, 200);
  }

  performSearch() {
    const query = this.inputTarget.value.trim();

    if (query.length < 2) {
      this.hideResults();
      return;
    }

    const results = this.fuse.search(query);
    this.selectedIndex = -1;

    if (results.length === 0) {
      this.resultsListTarget.innerHTML = "";
      this.noResultsTarget.classList.remove("hidden");
      this.resultsTarget.classList.remove("hidden");
      return;
    }

    this.noResultsTarget.classList.add("hidden");
    this.resultsListTarget.innerHTML = results
      .slice(0, 8)
      .map(
        (result, index) => `
      <a href="${result.item.path}"
         class="doc-search-result flex items-center gap-3 px-3 py-3 rounded-md text-sm hover:bg-indigo-50 transition-colors cursor-pointer ${index === this.selectedIndex ? "bg-indigo-50" : ""}"
         data-index="${index}"
         data-turbo-frame="_top">
        <div class="flex-1 min-w-0">
          <div class="font-medium text-gray-900">${result.item.title}</div>
          <div class="text-xs text-gray-500 truncate mt-0.5">${result.item.description}</div>
        </div>
        <svg class="w-4 h-4 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
        </svg>
      </a>
    `
      )
      .join("");

    this.resultsTarget.classList.remove("hidden");
  }

  onKeydown(event) {
    const resultElements = this.resultsListTarget.querySelectorAll(
      ".doc-search-result"
    );

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          resultElements.length - 1
        );
        this.highlightResult(resultElements);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.highlightResult(resultElements);
        break;
      case "Enter":
        event.preventDefault();
        if (
          this.selectedIndex >= 0 &&
          this.selectedIndex < resultElements.length
        ) {
          resultElements[this.selectedIndex].click();
        }
        break;
      case "Escape":
        this.hideResults();
        this.inputTarget.blur();
        break;
    }
  }

  highlightResult(elements) {
    elements.forEach((el, i) => {
      if (i === this.selectedIndex) {
        el.classList.add("bg-indigo-50");
      } else {
        el.classList.remove("bg-indigo-50");
      }
    });
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden");
  }
}
